// What the sidebar remembers between visits, with no idea what is inside it.
//
// Split out of AppLayout.vue for the same reason navigationRules.js was split
// out of navigation.js: folding is a rule with edge cases — a section that
// opens by itself while you are standing in it, a preference that has to keep
// working after somebody changes a default, a rail that must not swallow the
// entries a folded section was hiding — and a rule with edge cases belongs
// somewhere a test can reach without mounting a component.
//
// Nothing here touches the DOM. The caller hands in a storage object, which is
// `localStorage` in the application and a plain map in the tests.

/** Every fold in the sidebar in one map: sections by id, entries by route. */
export const FOLD_KEY = 'general.navGroups'

/** Whether the sidebar itself is collapsed to the icon rail. */
export const RAIL_KEY = 'general.navRail'

/**
 * Namespaced, so an entry at /quotes and a section whose id is 'quotes' cannot
 * end up sharing one remembered boolean.
 */
export const branchKey = (item) => `branch:${item.to}`

/** A stored map, or an empty one when there is nothing stored or it is corrupt. */
export function readFolds(storage) {
  try {
    const stored = JSON.parse(storage.getItem(FOLD_KEY))
    // `null`, an array or a number are all valid JSON and none of them is a map
    // of booleans. Anything but a plain object starts the user from the defaults
    // rather than crashing the shell on the first render.
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {}
  } catch {
    return {}
  }
}

/**
 * A copy of the map with one fold flipped, ready to be stored.
 *
 * `open` is the state as the user currently sees it rather than what the map
 * says, because a section can be open without being stored open — a section
 * that opened by itself, or one whose group default is open. Reading the map
 * here instead would make the first click on such a section appear to do
 * nothing.
 */
export function toggleFold(folds, key, open) {
  return { ...folds, [key]: !open }
}

export function writeFolds(storage, folds) {
  storage.setItem(FOLD_KEY, JSON.stringify(folds))
}

export const anyChildActive = (item, isActive) => (item.children ?? []).some(isActive)

// ── Where you are ────────────────────────────────────────────────────────────

/**
 * Whether the path is this entry's own page or one of the pages under it.
 *
 * The slash is the whole point: a plain `startsWith` makes /users light up for
 * /users42, which is a different screen belonging to nobody.
 */
const covers = (entry, path) =>
  entry.exact ? path === entry.to : path === entry.to || path.startsWith(`${entry.to}/`)

/**
 * The ONE entry the current page belongs to — the longest route that covers it.
 *
 * Asking each entry on its own whether it covers the path lights up two of them
 * whenever one route is inside another, and the navigation has two of those:
 *
 *   /production/boms   lit 'Órdenes de fabricación' (/production) as well as
 *                      'Escandallos', because the first is a prefix of the
 *                      second and both were answering for themselves.
 *   /workshop          lit both 'Producción' and its page 'Taller', which name
 *                      the same route on purpose — the row is the way in.
 *
 * The first case is fixed by the longest route winning. The second cannot be,
 * because the two routes are equal, so the pages are read before the row they
 * hang under and a tie leaves the page holding it: an entry you can see inside
 * is more precise about where you are than the folder around it.
 *
 * The entry itself comes back rather than its route, and the caller compares by
 * identity — for the /workshop pair, the route is exactly what does not tell
 * the two apart.
 */
export function activeEntry(groups, path) {
  let best = null

  for (const group of groups) {
    for (const item of group.items) {
      for (const entry of [...(item.children ?? []), item]) {
        if (covers(entry, path) && (!best || entry.to.length > best.to.length)) best = entry
      }
    }
  }

  return best
}

/** Whether the page you are on is one of this section's entries, or under one. */
export const holdsActive = (group, isActive) =>
  group.items.some(item => isActive(item) || anyChildActive(item, isActive))

/**
 * Whether a section is open: what the person decided, or — while they have
 * decided nothing — the section holding the page you are on, or the default.
 *
 * The order matters, and it used to be the other way round: the section you
 * were standing in was forced open and the chevron on it did nothing. That is
 * exactly backwards. Standing in a section is when its nine entries are most in
 * the way of the ten below them, and a fold that refuses to fold is not a fold.
 *
 * What it cost to force it open — nothing highlighted anywhere in the sidebar —
 * the heading pays back: `holdsActive` is what the shell marks a closed section
 * with, so 'you are inside this' survives the fold that hides the row.
 */
export function isGroupOpen(group, folds, isActive) {
  if (group.collapsible === false) return true
  return folds[group.id] ?? (holdsActive(group, isActive) || !group.startCollapsed)
}

/**
 * The same, one level in, for an entry that holds other entries.
 *
 * The default is closed: the whole point of the fold is that a module with six
 * screens costs one row until somebody wants those six. Standing on one of them
 * opens it by itself the first time — and then the person may close it again,
 * for the same reason as above. The row it hangs under carries the mark while
 * it is closed.
 */
export function isBranchOpen(item, folds, isActive) {
  return folds[branchKey(item)] ?? anyChildActive(item, isActive)
}

// ── The sidebar itself ───────────────────────────────────────────────────────

/**
 * Whether the person left the sidebar collapsed.
 *
 * Stored as its own key rather than inside the fold map: it is a different kind
 * of preference — how much of the window the navigation is allowed to take,
 * not which parts of it are interesting — and one day a client will want a
 * default for it that has nothing to do with anybody's folds.
 */
export const readRail = (storage) => storage.getItem(RAIL_KEY) === '1'

export const writeRail = (storage, railed) => storage.setItem(RAIL_KEY, railed ? '1' : '0')

/**
 * What the rail shows: every entry, from every section, folded or not.
 *
 * Honouring the folds here would hide entries behind section headings that the
 * rail does not draw, and the platform section — which starts folded — would
 * have no way back. A rail is a complete index of the sidebar or it is a trap.
 *
 * The children stay behind their parent, in the flyout: a rail is one icon per
 * destination you can name, and a module's six screens do not have six icons.
 */
export function railItems(groups) {
  return groups.flatMap((group, index) =>
    group.items.map((item, position) => ({
      // The entry itself, not a copy of it. `activeEntry` above hands back one
      // of these objects and the shell asks 'is this that one?' — a spread here
      // would make every rail icon a different object from the one the answer
      // refers to, and nothing would ever be marked as current.
      item,
      // The first entry of a section that is not the first section: the only
      // place the rail draws a line, so the grouping survives losing the
      // headings that carried it.
      startsGroup: index > 0 && position === 0,
    }))
  )
}
